export type CodecSupport = 'supported' | 'maybe' | 'unsupported' | 'unknown'

export interface CodecCapabilities {
  h264: CodecSupport
  hevc: CodecSupport
  vp9: CodecSupport
  av1: CodecSupport
  aac: CodecSupport
  ac3: CodecSupport
  eac3: CodecSupport
  opus: CodecSupport
  hls: CodecSupport
  source: string
  testedAt: number
}

export const UNKNOWN_CODEC_CAPABILITIES: CodecCapabilities = {
  h264: 'unknown', hevc: 'unknown', vp9: 'unknown', av1: 'unknown',
  aac: 'unknown', ac3: 'unknown', eac3: 'unknown', opus: 'unknown', hls: 'unknown',
  source: 'not probed', testedAt: 0,
}

export function codecSupport(value: string): CodecSupport {
  if (value === 'probably') return 'supported'
  if (value === 'maybe') return 'maybe'
  return 'unsupported'
}

/** Read the exact HTML media hints exposed by this webOS Chromium runtime.
 * These remain hints—the packaged/live fixtures are stronger evidence—but
 * ranking should not use a generic TV profile when the actual TV answered. */
export function detectCodecCapabilities(): CodecCapabilities {
  // Keep this module type-safe in the Worker build too: remoteProtocol shares
  // CodecCapabilities but the Worker intentionally has no DOM library.
  const browser = globalThis as typeof globalThis & {
    document?: { createElement(tag: string): { canPlayType(value: string): string } }
  }
  if (!browser.document) return UNKNOWN_CODEC_CAPABILITIES
  const video = browser.document.createElement('video')
  const audio = browser.document.createElement('audio')
  return {
    h264: codecSupport(video.canPlayType('video/mp4; codecs="avc1.640028"')),
    hevc: codecSupport(video.canPlayType('video/mp4; codecs="hvc1.1.6.L123.B0"')),
    vp9: codecSupport(video.canPlayType('video/webm; codecs="vp9"')),
    av1: codecSupport(video.canPlayType('video/mp4; codecs="av01.0.08M.08"')),
    aac: codecSupport(audio.canPlayType('audio/mp4; codecs="mp4a.40.2"')),
    ac3: codecSupport(audio.canPlayType('audio/mp4; codecs="ac-3"')),
    eac3: codecSupport(audio.canPlayType('audio/mp4; codecs="ec-3"')),
    opus: codecSupport(audio.canPlayType('audio/webm; codecs="opus"')),
    hls: codecSupport(video.canPlayType('application/vnd.apple.mpegurl')),
    source: 'HTMLMediaElement.canPlayType',
    testedAt: Date.now(),
  }
}
